import { useMemo, useState } from "react";
import { BlockView, PageRuntime } from "../blocks/blocks.tsx";
import { ApiError } from "../lib/api.ts";
import { proposeAndApply } from "../lib/changes.ts";
import { draftOf, moveAfter, newBlockId, pageOps, type Draft } from "../lib/diff.ts";
import { navigate } from "../lib/router.ts";
import { useSession } from "../lib/session.tsx";
import type { BlockDef, CollectionDef, PageDef } from "../lib/types.ts";
import { Props } from "./props.tsx";

const PALETTE: { type: BlockDef["type"]; label: string; hint: string }[] = [
  { type: "table", label: "Tabela", hint: "lista registros" },
  { type: "form", label: "Formulário", hint: "cria registros" },
  { type: "details", label: "Detalhe", hint: "um registro" },
  { type: "metric", label: "Indicador", hint: "contagem, soma…" },
  { type: "kanban", label: "Kanban", hint: "colunas por status" },
  { type: "text", label: "Texto", hint: "instruções" },
];
const TYPE_LABEL = Object.fromEntries(PALETTE.map((p) => [p.type, p.label])) as Record<BlockDef["type"], string>;

/** A new block of a type, with a sensible collection. Null when the type cannot be used yet. */
export function makeBlock(type: BlockDef["type"], collections: CollectionDef[], taken: BlockDef[]): BlockDef | null {
  const id = newBlockId(taken);
  if (type === "text") return { id, type, width: "full", content: "Escreva aqui." };
  if (type === "kanban") {
    const c = collections.find((x) => x.fields.some((f) => f.interface === "select"));
    if (!c) return null;
    return { id, type, width: "full", collection: c.name, groupBy: c.fields.find((f) => f.interface === "select")!.name };
  }
  const c = collections[0];
  if (!c) return null;
  if (type === "details") {
    const source = taken.find((b) => b.type === "table" && b.collection === c.name);
    return { id, type, width: "half", collection: c.name, ...(source ? { source: source.id } : {}) };
  }
  if (type === "metric") return { id, type, width: "half", collection: c.name, agg: "count" };
  return { id, type, width: "full", collection: c.name } as BlockDef;
}

export function Builder({ page }: { page: PageDef }) {
  const s = useSession();
  const published = useMemo(() => draftOf(page), [page]);
  const [draft, setDraft] = useState<Draft>(published);
  const [sel, setSel] = useState<string | null>(null);
  const [drop, setDrop] = useState<{ id: string; pos: "before" | "after" } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ops = useMemo(() => pageOps(published, draft), [published, draft]);
  const selected = draft.blocks.find((b) => b.id === sel) ?? null;

  const setBlocks = (f: (b: BlockDef[]) => BlockDef[]) => setDraft((d) => ({ ...d, blocks: f(d.blocks) }));

  function add(type: BlockDef["type"], after?: string | null) {
    const b = makeBlock(type, s.collections, draft.blocks);
    if (!b) {
      setErr(type === "kanban" ? "O kanban precisa de uma coleção com um campo de seleção." : "Crie uma coleção antes de usar este bloco.");
      return;
    }
    setErr(null);
    setBlocks((list) => {
      const i = after === undefined ? list.length : after === null ? 0 : list.findIndex((x) => x.id === after) + 1;
      return [...list.slice(0, i), b, ...list.slice(i)];
    });
    setSel(b.id);
  }

  function onDrop(e: React.DragEvent, target: string | null) {
    e.preventDefault();
    e.stopPropagation();
    const pos = drop;
    setDrop(null);
    const blocks = draft.blocks;
    // The block it lands after: the one before the target when dropping above it.
    let after: string | null = blocks.at(-1)?.id ?? null;
    if (target && pos) {
      const i = blocks.findIndex((b) => b.id === target);
      after = pos.pos === "after" ? target : i > 0 ? blocks[i - 1]!.id : null;
    }
    const newType = e.dataTransfer.getData("text/x-new-block") as BlockDef["type"];
    const moving = e.dataTransfer.getData("text/x-block");
    if (newType) add(newType, after);
    else if (moving && moving !== after) setBlocks((list) => moveAfter(list, moving, after));
  }

  async function publish() {
    setBusy(true);
    setErr(null);
    try {
      const { changeset, applied } = await proposeAndApply(`Editar a tela ${draft.title}`, ops);
      await s.reloadStructure();
      if (applied) {
        s.toast("Tela publicada.");
        navigate(`/t/${page.name}`);
      } else navigate(`/changesets/${changeset.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? [e.message, ...e.details.map((d) => d.message)].join(" ") : "Não foi possível publicar.");
    } finally {
      setBusy(false);
    }
  }

  function discard() {
    if (ops.length && !confirm("Descartar as mudanças desta tela?")) return;
    navigate(`/t/${page.name}`);
  }

  const idx = (id: string) => draft.blocks.findIndex((b) => b.id === id);

  return (
    <PageRuntime collections={s.collections}>
      <div className="topbar">
        <input
          className="input"
          aria-label="Título da tela"
          style={{ fontSize: 20, fontWeight: 700, maxWidth: 360 }}
          value={draft.title}
          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
        />
        <span className={`draft-pill ${ops.length ? "" : "clean"}`}>{ops.length ? `${ops.length} ${ops.length === 1 ? "mudança" : "mudanças"} não publicadas` : "sem mudanças"}</span>
        <span className="spacer" />
        <button className="btn" onClick={discard}>
          {ops.length ? "Descartar" : "Sair da edição"}
        </button>
        <button className="btn primary" onClick={publish} disabled={busy || !ops.length || !draft.title.trim()}>
          Publicar
        </button>
      </div>
      {err && <p className="error">{err}</p>}
      <div className="builder">
        <div className="palette" aria-label="Blocos">
          {PALETTE.map((p) => (
            <button
              key={p.type}
              className="pal"
              draggable
              onDragStart={(e) => e.dataTransfer.setData("text/x-new-block", p.type)}
              onClick={() => add(p.type)}
              title="Clique ou arraste para a tela"
            >
              <span>
                {p.label}
                <small>{p.hint}</small>
              </span>
            </button>
          ))}
        </div>

        <div
          className="grid editing"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => onDrop(e, null)}
          onClick={() => setSel(null)}
        >
          {draft.blocks.map((b) => (
            <div
              key={b.id}
              className={`blk card ${b.width} ${sel === b.id ? "sel" : ""} ${drop?.id === b.id ? `drop-${drop.pos}` : ""}`}
              draggable
              onDragStart={(e) => {
                e.stopPropagation();
                e.dataTransfer.setData("text/x-block", b.id);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const r = e.currentTarget.getBoundingClientRect();
                const pos = e.clientY < r.top + r.height / 2 ? "before" : "after";
                if (drop?.id !== b.id || drop.pos !== pos) setDrop({ id: b.id, pos });
              }}
              onDragLeave={() => setDrop(null)}
              onDrop={(e) => onDrop(e, b.id)}
              onClick={(e) => {
                e.stopPropagation();
                setSel(b.id);
              }}
            >
              <span className="tag">{TYPE_LABEL[b.type]}</span>
              {sel === b.id && (
                <span className="bar">
                  <button title="Subir" disabled={idx(b.id) === 0} onClick={() => setBlocks((l) => moveAfter(l, b.id, idx(b.id) > 1 ? l[idx(b.id) - 2]!.id : null))}>
                    ↑
                  </button>
                  <button title="Descer" disabled={idx(b.id) === draft.blocks.length - 1} onClick={() => setBlocks((l) => moveAfter(l, b.id, l[idx(b.id) + 1]!.id))}>
                    ↓
                  </button>
                  <button
                    title="Remover"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBlocks((l) => l.filter((x) => x.id !== b.id).map((x) => (x.type === "details" && x.source === b.id ? { ...x, source: undefined } : x)));
                      setSel(null);
                    }}
                  >
                    Remover
                  </button>
                </span>
              )}
              <div className="inert">
                <BlockView block={b} />
              </div>
            </div>
          ))}
          {!draft.blocks.length && <div className="empty">Arraste blocos da esquerda para cá, ou clique neles.</div>}
        </div>

        <aside className="props card" aria-label="Propriedades">
          {selected ? (
            <Props
              key={selected.id}
              block={selected}
              blocks={draft.blocks}
              collections={s.collections}
              onChange={(nb) => setBlocks((l) => l.map((x) => (x.id === nb.id ? nb : x)))}
            />
          ) : (
            <div className="stack">
              <strong>Tela</strong>
              <p className="muted" style={{ margin: 0 }}>
                Clique num bloco para editar. Publicar cria um changeset com as mudanças e aplica na hora; ele fica no histórico de Mudanças e pode ser desfeito.
              </p>
              {ops.length > 0 && (
                <details>
                  <summary className="muted">Operações ({ops.length})</summary>
                  <pre className="code">{JSON.stringify(ops, null, 2)}</pre>
                </details>
              )}
            </div>
          )}
        </aside>
      </div>
    </PageRuntime>
  );
}
